import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RiepilogoModuli as RiepilogoData, ModuloKey } from "../../api/dashboard";
import { SECTION_COLORS, SectionKey } from "../../utils/sectionColors";

const MODULI: { key: ModuloKey; label: string; section: SectionKey }[] = [
  { key: "installazioni", label: "Installazioni", section: "installazioni" },
  { key: "sopralluoghi", label: "Sopralluoghi", section: "sopralluoghi" },
  { key: "interventi", label: "Interventi", section: "interventi" },
  { key: "attivazioni", label: "Attivazioni", section: "attivazioni" },
];

/** "2026-07" → "lug 26": asse leggibile senza ruotare le etichette. */
function etichettaMese(mese: string): string {
  const [anno, m] = mese.split("-");
  const nomi = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  return `${nomi[Number(m) - 1] ?? m} ${anno.slice(2)}`;
}

/**
 * Andamento degli ultimi 12 mesi dei quattro moduli sullo stesso asse.
 *
 * Confrontabile perché le serie arrivano già allineate dal backend — dodici
 * mesi completi anche dove il valore è zero — e perché ogni modulo è contato
 * sulla propria data di dominio (per le installazioni la data effettiva o
 * l'ipotesi, per gli interventi il completamento o la data prevista, e così
 * via): è la stessa regola del calendario unificato.
 *
 * I record senza data non compaiono qui: non appartengono a nessun mese. Il
 * loro numero resta nelle card di riepilogo, che dichiarano "N senza data".
 */
export function AndamentoModuli({ dati }: { dati: RiepilogoData }) {
  const mesi = dati.installazioni.serie.map((p) => p.mese);

  const righe = mesi.map((mese, i) => {
    const riga: Record<string, string | number> = { mese: etichettaMese(mese) };
    for (const m of MODULI) riga[m.key] = dati[m.key].serie[i]?.n ?? 0;
    return riga;
  });

  const totaleComplessivo = MODULI.reduce((s, m) => s + dati[m.key].totale, 0);
  if (totaleComplessivo === 0) {
    return <p className="py-10 text-center text-sm text-ink/40">Nessun dato da mostrare.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={righe} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(24,39,61,0.08)" />
        <XAxis dataKey="mese" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
        <Tooltip />
        <Legend />
        {MODULI.map((m) => (
          <Line
            key={m.key}
            type="monotone"
            dataKey={m.key}
            name={m.label}
            stroke={SECTION_COLORS[m.section].base}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
